import { Capacitor } from "@capacitor/core";
import { CapacitorUpdater } from "@capgo/capacitor-updater";
import { env } from "@/lib/env";

interface VersionInfo {
  version: string;
  url: string;
  buildDate: string;
}

export class AppUpdaterService {
  private static instance: AppUpdaterService;
  private updateCheckInterval: number = 30 * 60 * 1000; // 30 minutes
  private isNative = Capacitor.isNativePlatform();

  static getInstance(): AppUpdaterService {
    if (!AppUpdaterService.instance) {
      AppUpdaterService.instance = new AppUpdaterService();
    }
    return AppUpdaterService.instance;
  }

  /**
   * Initialize the updater service
   */
  async initialize(): Promise<void> {
    if (!this.isNative) {
      return;
    }

    if (!env.updateUrl) {
      return;
    }

    // Clear any pending updates on startup to prevent restart loops
    await this.clearPendingUpdates();
    await this.checkForUpdate();
    setInterval(() => {
      this.checkForUpdate();
    }, this.updateCheckInterval);
  }

  /**
   * Check for available updates
   */
  async checkForUpdate(): Promise<void> {
    if (!this.isNative || !env.updateUrl) {
      return;
    }

    try {
      const startTime = Date.now();

      const response = await fetch(env.updateUrl, { cache: "no-store" });
      const _fetchTime = Date.now() - startTime;

      if (!response.ok) {
        throw new Error(`Failed to fetch version info: ${response.status} ${response.statusText}`);
      }

      const versionInfo: VersionInfo = await response.json();

      const currentVersion = await this.getCurrentVersion();

      const needsUpdate = this.shouldUpdate(currentVersion, versionInfo.version);

      if (needsUpdate) {
        await this.downloadAndInstallUpdate(versionInfo);
      } else {
      }
    } catch (error) {
      console.error("[AppUpdater] ❌ Error checking for updates:", error);
      console.error("[AppUpdater] 🔍 Error details:", {
        name: error?.name,
        message: error?.message,
        stack: error?.stack?.split("\n").slice(0, 3).join("\n"),
      });
    }
  }

  /**
   * Download and install an update
   */
  private async downloadAndInstallUpdate(versionInfo: VersionInfo): Promise<void> {
    try {
      const downloadStartTime = Date.now();

      // Download the update bundle
      const { id } = await CapacitorUpdater.download({
        url: versionInfo.url,
        version: versionInfo.version,
      });

      const _downloadTime = Date.now() - downloadStartTime;
      await CapacitorUpdater.set({
        id: id,
      });

      // Optionally notify the user
      this.notifyUpdateReady();
    } catch (error) {
      console.error("[AppUpdater] ❌ Error downloading/installing update:", error);
      console.error("[AppUpdater] 🔍 Download error details:", {
        url: versionInfo.url,
        version: versionInfo.version,
        error: error?.message,
      });
    }
  }

  /**
   * Get current app version
   */
  async getCurrentVersion(): Promise<string> {
    try {
      const current = await CapacitorUpdater.current();
      return current.bundle.version || "0.0.0";
    } catch (error) {
      console.warn("[AppUpdater] ⚠️ Could not get current version:", error);
      return "0.0.0";
    }
  }

  /**
   * Compare versions to determine if update is needed.
   * Only updates when the server bundle is strictly newer than what is running,
   * so a newer native binary is never downgraded to an older OTA bundle.
   */
  private shouldUpdate(currentVersion: string, latestVersion: string): boolean {
    // A fresh native install has no OTA bundle applied yet ("builtin"); its
    // effective version is the one baked into the binary at build time.
    const effectiveCurrent = currentVersion === "builtin" ? env.appVersion : currentVersion;

    return this.isNewerVersion(latestVersion, effectiveCurrent);
  }

  /**
   * Returns true when `candidate` is a strictly higher semantic version than
   * `base`. Missing segments are treated as 0; equal versions return false.
   */
  private isNewerVersion(candidate: string, base: string): boolean {
    const candidateParts = candidate.split(".").map((part) => Number(part) || 0);
    const baseParts = base.split(".").map((part) => Number(part) || 0);

    for (let i = 0; i < Math.max(candidateParts.length, baseParts.length); i++) {
      const candidatePart = candidateParts[i] || 0;
      const basePart = baseParts[i] || 0;

      if (candidatePart > basePart) {
        return true;
      }
      if (candidatePart < basePart) {
        return false;
      }
    }

    return false;
  }

  /**
   * Notify user that an update is ready
   */
  private notifyUpdateReady(): void {
    // Optional: Show a toast or modal to inform the user
    // This could integrate with your existing notification system
  }

  /**
   * Clear any pending updates to prevent restart loops
   */
  private async clearPendingUpdates(): Promise<void> {
    try {
      const current = await CapacitorUpdater.current();

      if (current.bundle.status === "pending") {
        // Notify Capacitor Updater that the current bundle is working fine
        await CapacitorUpdater.notifyAppReady();
      } else {
      }
    } catch (error) {
      console.warn("[AppUpdater] ⚠️ Error clearing pending updates:", error);
    }
  }

  /**
   * Force manual update check (for testing)
   */
  async forceUpdateCheck(): Promise<void> {
    await this.checkForUpdate();
  }

  /**
   * Force restart the app to apply updates
   */
  async restartApp(): Promise<void> {
    if (!this.isNative) return;

    try {
      await CapacitorUpdater.reload();
    } catch (error) {
      console.error("[AppUpdater] Error restarting app:", error);
    }
  }

  /**
   * Get information about available updates
   */
  async getUpdateInfo(): Promise<{ current: string; available?: string; hasUpdate: boolean }> {
    try {
      const currentVersion = await this.getCurrentVersion();

      if (!this.isNative || !env.updateUrl) {
        return { current: currentVersion, hasUpdate: false };
      }

      const response = await fetch(env.updateUrl);
      if (!response.ok) {
        return { current: currentVersion, hasUpdate: false };
      }

      const versionInfo: VersionInfo = await response.json();
      const hasUpdate = this.shouldUpdate(currentVersion, versionInfo.version);

      return {
        current: currentVersion,
        available: versionInfo.version,
        hasUpdate,
      };
    } catch (error) {
      console.error("[AppUpdater] Error getting update info:", error);
      return { current: "0.0.0", hasUpdate: false };
    }
  }
}
